/**
 * @function useProductInfo
 * @author hzihhuang
 * @description
 * 通过产品 ID 获取产品信息，并提供智能的类型推断与字段映射。
 *
 * 适用于需要根据 productId 和 variantId 异步获取产品详情的场景，
 * 并可通过 options 实现字段 key 的自定义映射。
 *
 * 【使用示例】
 *
 * // 示例产品列表
 * const productList = [
 *   { productId: 123, variantId: 456 },
 *   { productId: 789, variantId: 101 },
 * ] as const;
 *
 * // 1️⃣ 默认使用（返回 ApiResponse 中所有字段，字段名不变）
 * const result1 = useProductInfo(productList);
 *
 * // 2️⃣ 除 productId / variantId 外，传入额外字段（保留原字段 + 自动合并响应字段）
 * const productListWithExtra = [
 *   { productId: 123, variantId: 456, foo: 'bar' },
 * ];
 * const result2 = useProductInfo(productListWithExtra);
 *
 * // 3️⃣ 增加 options，映射字段名（如 sku -> skuCode, price -> salePrice）
 * const options = {
 *   sku: 'skuCode',
 *   price: 'salePrice',
 * } as const;
 * const result3 = useProductInfo(productList, options);
 * // 返回中包含 { skuCode, salePrice }，而不是原始的 sku 和 price
 *
 * // 4️⃣ 传入第三个参数 keepOriginalData = true（保留原始字段 + 映射字段）
 * const result4 = useProductInfo(productList, options, true);
 * // 返回中同时包含：sku、skuCode，price、salePrice 等字段
 *
 * 【类型自动推导说明】
 * - 支持按 options 自动推导字段名
 * - 支持推断是否保留原始字段
 * - 保证类型安全，不影响额外扩展字段
 *
 * @param productInfo 产品 ID 与变体 ID 列表，支持附加自定义字段
 * @param options 字段映射规则，例如 { sku: 'skuCode' }
 * @param keepOriginalData 是否保留原始字段，默认 false（只返回映射字段）
 * @returns 映射后的产品信息列表，类型自动推导
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Options, ProductIdentifier, WithOriginal, WithOriginalAndMapping } from './type';
import getProductInfo from './getProductInfo';
import getLocal from '@/app/utils/i18n';

function useProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    options: O,
    keepOriginalData: true,
): WithOriginalAndMapping<T, O>[];

function useProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(
    productInfo: T[],
    options: O,
    keepOriginalData: false,
): WithOriginal<T, O>[];

function useProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(productInfo: T[], options?: O): WithOriginal<T, O>[];

function useProductInfo<T extends ProductIdentifier & Record<string, any>, O extends Options>(productInfo: T[], options?: O, keepOriginalData = false): any {
    const [list, setList] = useState(productInfo);
    const { locale = 'us' } = getLocal();

    const enrich = () => {
        getProductInfo(productInfo, locale, options ?? {}, keepOriginalData as false)
            .then(setList)
            .catch(console.error);
    };

    useEffect(() => {
        enrich();
    }, []);
    return list;
}

export { default as getProductInfo } from './getProductInfo';
export default useProductInfo;
